import { getSession } from "./session";

export interface FriendView {
  readonly friendshipId: string;
  readonly userId: string;
  readonly email: string;
  readonly nickname: string;
  readonly since: number;
}

export interface FriendRequestView {
  readonly requestId: string;
  readonly userId: string;
  readonly email: string;
  readonly nickname: string;
  readonly createdAt: number;
}

export interface FriendState {
  readonly friends: readonly FriendView[];
  readonly incoming: readonly FriendRequestView[];
  readonly outgoing: readonly FriendRequestView[];
}

export interface FriendClient {
  list(): Promise<FriendState>;
  request(targetNickname: string): Promise<FriendState>;
  accept(requestId: string): Promise<FriendState>;
  reject(requestId: string): Promise<FriendState>;
  remove(friendUserId: string): Promise<FriendState>;
}

export class FriendApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "FriendApiError";
  }
}

export class RemoteFriendClient implements FriendClient {
  constructor(
    private readonly baseUrl: string,
    private readonly token: string,
    private readonly fetcher: typeof fetch = fetch,
  ) {}

  list(): Promise<FriendState> {
    return this.requestState("/friends");
  }

  request(targetNickname: string): Promise<FriendState> {
    return this.requestState("/friends/requests", {
      method: "POST",
      body: JSON.stringify({ targetNickname: normalizeFriendNickname(targetNickname) }),
    });
  }

  accept(requestId: string): Promise<FriendState> {
    return this.respond(requestId, "accept");
  }

  reject(requestId: string): Promise<FriendState> {
    return this.respond(requestId, "reject");
  }

  remove(friendUserId: string): Promise<FriendState> {
    return this.requestState(`/friends/${encodeURIComponent(identifier(friendUserId))}`, {
      method: "DELETE",
    });
  }

  private respond(
    requestId: string,
    response: "accept" | "reject",
  ): Promise<FriendState> {
    return this.requestState(
      `/friends/requests/${encodeURIComponent(identifier(requestId))}/${response}`,
      { method: "POST", body: "{}" },
    );
  }

  private async requestState(
    path: string,
    init: RequestInit = {},
  ): Promise<FriendState> {
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${this.token}`);
    if (init.body) headers.set("content-type", "application/json");
    let response: Response;
    try {
      response = await this.fetcher.call(
        globalThis,
        `${this.baseUrl.replace(/\/$/u, "")}${path}`,
        { ...init, headers },
      );
    } catch {
      throw new FriendApiError("无法连接好友服务", "NETWORK", 0);
    }
    if (!response.ok) throw await responseError(response);
    return parseFriendState(await response.json());
  }
}

export function createFriendClient(): FriendClient | null {
  const configured = (import.meta.env.VITE_GAME_API_URL ??
    import.meta.env.VITE_AUTH_API_URL) as string | undefined;
  const session = getSession();
  if (!configured || !session || session.token.startsWith("local:")) return null;
  const baseUrl = configured.replace(/\/auth\/?$/u, "");
  return new RemoteFriendClient(baseUrl, session.token);
}

export function parseFriendState(value: unknown): FriendState {
  const state = strictRecord(value, ["friends", "incoming", "outgoing"], "好友状态");
  const friends = strictArray(state.friends, parseFriend, "好友列表");
  const incoming = strictArray(state.incoming, parseFriendRequest, "收到的好友申请");
  const outgoing = strictArray(state.outgoing, parseFriendRequest, "发出的好友申请");
  const relationIds = [
    ...friends.map((entry) => entry.friendshipId),
    ...incoming.map((entry) => entry.requestId),
    ...outgoing.map((entry) => entry.requestId),
  ];
  if (relationIds.length !== new Set(relationIds).size) {
    throw new FriendApiError("好友状态包含重复关系", "INVALID_RESPONSE", 502);
  }
  return { friends, incoming, outgoing };
}

function parseFriend(value: unknown): FriendView {
  const row = strictRecord(
    value,
    ["email", "friendshipId", "nickname", "since", "userId"],
    "好友记录",
  );
  return {
    friendshipId: responseIdentifier(row.friendshipId),
    userId: responseIdentifier(row.userId),
    email: responseEmail(row.email),
    nickname: responseNickname(row.nickname),
    since: responseTimestamp(row.since),
  };
}

function parseFriendRequest(value: unknown): FriendRequestView {
  const row = strictRecord(
    value,
    ["createdAt", "email", "nickname", "requestId", "userId"],
    "好友申请",
  );
  return {
    requestId: responseIdentifier(row.requestId),
    userId: responseIdentifier(row.userId),
    email: responseEmail(row.email),
    nickname: responseNickname(row.nickname),
    createdAt: responseTimestamp(row.createdAt),
  };
}

function strictRecord(
  value: unknown,
  expectedKeys: readonly string[],
  label: string,
): Readonly<Record<string, unknown>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new FriendApiError(`${label}响应无效`, "INVALID_RESPONSE", 502);
  }
  const record = value as Readonly<Record<string, unknown>>;
  if (Object.keys(record).sort().join("\0") !== [...expectedKeys].sort().join("\0")) {
    throw new FriendApiError(`${label}字段无效`, "INVALID_RESPONSE", 502);
  }
  return record;
}

function strictArray<T>(
  value: unknown,
  parser: (entry: unknown) => T,
  label: string,
): T[] {
  if (!Array.isArray(value) || value.length > 500) {
    throw new FriendApiError(`${label}响应无效`, "INVALID_RESPONSE", 502);
  }
  return value.map(parser);
}

function normalizeFriendEmail(value: string): string {
  const email = value.normalize("NFKC").trim().toLowerCase();
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) {
    throw new FriendApiError("请输入有效好友邮箱", "INVALID_REQUEST", 400);
  }
  return email;
}

function normalizeFriendNickname(value: string): string {
  const nickname = value.normalize("NFKC").trim();
  if (nickname.length < 2 || nickname.length > 24)
    throw new FriendApiError("请输入有效好友昵称", "INVALID_REQUEST", 400);
  return nickname;
}

function identifier(value: string): string {
  const result = value.trim();
  if (result.length < 1 || result.length > 128) {
    throw new FriendApiError("好友标识无效", "INVALID_REQUEST", 400);
  }
  return result;
}

function responseIdentifier(value: unknown): string {
  if (typeof value !== "string" || value.length < 1 || value.length > 128) {
    throw new FriendApiError("好友标识响应无效", "INVALID_RESPONSE", 502);
  }
  return value;
}

function responseEmail(value: unknown): string {
  if (typeof value !== "string") {
    throw new FriendApiError("好友邮箱响应无效", "INVALID_RESPONSE", 502);
  }
  try {
    return normalizeFriendEmail(value);
  } catch {
    throw new FriendApiError("好友邮箱响应无效", "INVALID_RESPONSE", 502);
  }
}

function responseNickname(value: unknown): string {
  if (typeof value !== "string" || value.trim().length < 1 || value.length > 24) {
    throw new FriendApiError("好友昵称响应无效", "INVALID_RESPONSE", 502);
  }
  return value.trim();
}

function responseTimestamp(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new FriendApiError("好友时间响应无效", "INVALID_RESPONSE", 502);
  }
  return value;
}

async function responseError(response: Response): Promise<FriendApiError> {
  let code = `HTTP_${response.status}`;
  let message = response.status === 401
    ? "登录已失效"
    : response.status === 403
      ? "无权处理该好友关系"
      : "好友操作失败";
  try {
    const payload = (await response.json()) as {
      error?: { code?: unknown; message?: unknown };
    };
    if (typeof payload.error?.code === "string") code = payload.error.code;
    if (typeof payload.error?.message === "string") message = payload.error.message;
  } catch {
    // Use the status-based fallback.
  }
  return new FriendApiError(message, code, response.status);
}
